import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";

const SLIDES = [
  {
    id: "design-material-selection",
    name: "Design_&_Material_Selection.webp",
    title: "Design & Material Selection",
    tagline: "Custom Mold Tooling, Raw Carbon & Material Engineering",
    description:
      "T700 raw carbon fiber, honeycomb poly cores, fiberglass composites, custom perimeter edge guards, and custom Pantone color specs.",
    image: "/images/manufacturing/Design_&_Material_Selection.webp",
    badge: "Step 01 — Design & Materials",
  },
  {
    id: "manufacturing-process",
    name: "Manufacturing_Process.webp",
    title: "Manufacturing Process",
    tagline: "Precision Engineering & Modern Assembly Lines",
    description:
      "Thermoforming, cold-press lamination, CNC trimming, and rotational molding engineered to international tournament standards.",
    image: "/images/manufacturing/Manufacturing_Process.webp",
    badge: "Step 02 — Production",
  },
  {
    id: "quality-inspection",
    name: "quality_inspection.webp",
    title: "Quality Inspection",
    tagline: "Batch-Level QC & Rigorous Testing",
    description:
      "Detailed surface inspection, weight & balance tolerance checks, core bonding verification, ball bounce tests, and AQL documentation.",
    image: "/images/manufacturing/quality_inspection.webp",
    badge: "Step 03 — Inspection",
  },
  {
    id: "packaging-shipping",
    name: "packing.webp",
    title: "Packaging & Shipping",
    tagline: "Retail-Ready Packaging & Global Export Protection",
    description:
      "Custom branded retail boxes, barcode labelling, neoprene paddle covers, shrink wrapping, and drop-tested export palletization.",
    image: "/images/manufacturing/packing.webp",
    badge: "Step 04 — Packing & Shipping",
  },
];

const ManufacturingCarousel = () => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);

  const goTo = useCallback((idx, dir = 1) => {
    setDirection(dir);
    setCurrent((idx + SLIDES.length) % SLIDES.length);
  }, []);

  const next = useCallback(() => goTo(current + 1, 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1, -1), [current, goTo]);

  // Autoplay every 5 seconds when not hovered
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next, isPaused]);

  const slide = SLIDES[current];

  const variants = {
    enter: (dir) => ({
      opacity: 0,
      x: dir > 0 ? 50 : -50,
      scale: 1.02,
    }),
    center: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: { duration: 0.5, ease: [0.25, 1, 0.5, 1] },
    },
    exit: (dir) => ({
      opacity: 0,
      x: dir > 0 ? -50 : 50,
      scale: 0.98,
      transition: { duration: 0.4, ease: [0.25, 1, 0.5, 1] },
    }),
  };

  return (
    <div
      className="relative mt-12 overflow-hidden border border-white/15 bg-black"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="manufacturing-carousel"
    >
      {/* Slide Image Container */}
      <div className="relative aspect-[16/9] md:aspect-[21/9] min-h-[380px] md:min-h-[500px] w-full overflow-hidden">
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.div
            key={slide.id}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            className="absolute inset-0 h-full w-full"
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="h-full w-full object-cover object-center"
            />
            {/* Gradient Overlays for readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-black/30" />
          </motion.div>
        </AnimatePresence>

        {/* Slide Content Overlay */}
        <div className="absolute bottom-0 left-0 right-0 z-20 p-6 md:p-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-none bg-brand-red px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-widest text-white">
              <CheckCircle2 size={13} />
              <span>{slide.badge}</span>
            </div>
            <h3 className="mt-3 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl md:text-4xl">
              {slide.title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-white/80 sm:text-base">
              {slide.description}
            </p>
          </div>
        </div>

        {/* Arrow Navigation Controls */}
        <div className="absolute top-6 right-6 z-30 flex items-center gap-2">
          <button
            onClick={prev}
            aria-label="Previous slide"
            className="flex h-10 w-10 md:h-12 md:w-12 items-center justify-center border border-white/20 bg-black/60 text-white backdrop-blur-md transition-all hover:bg-brand-red hover:border-brand-red"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={next}
            aria-label="Next slide"
            className="flex h-10 w-10 md:h-12 md:w-12 items-center justify-center border border-white/20 bg-black/60 text-white backdrop-blur-md transition-all hover:bg-brand-red hover:border-brand-red"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Interactive Bottom Slide Selector Tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 border-t border-white/15 bg-brand-ink/90 divide-x divide-white/15 divide-y sm:divide-y-0">
        {SLIDES.map((s, idx) => {
          const isActive = idx === current;
          return (
            <button
              key={s.id}
              onClick={() => goTo(idx, idx > current ? 1 : -1)}
              className={`group flex flex-col p-3 sm:p-4 text-left transition-all ${
                isActive ? "bg-white/10" : "hover:bg-white/5"
              }`}
            >
              <div className="flex items-center justify-between w-full">
                <span
                  className={`font-mono text-[11px] uppercase tracking-wider ${
                    isActive ? "text-brand-red font-bold" : "text-white/40 group-hover:text-white/70"
                  }`}
                >
                  0{idx + 1}
                </span>
                <span
                  className={`hidden sm:inline-block font-mono text-[10px] uppercase tracking-wider text-white/40 truncate max-w-[100px]`}
                >
                  {s.name}
                </span>
              </div>
              <span
                className={`mt-1 font-display text-xs sm:text-sm font-medium tracking-tight truncate ${
                  isActive ? "text-white" : "text-white/60 group-hover:text-white"
                }`}
              >
                {s.title}
              </span>
              {/* Progress Bar Indicator */}
              <div className="mt-2 h-[2px] w-full bg-white/10 overflow-hidden">
                <div
                  className={`h-full bg-brand-red transition-all duration-300 ${
                    isActive ? "w-full" : "w-0 group-hover:w-1/3"
                  }`}
                />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ManufacturingCarousel;
